import { theme } from '@Guidelines/Theme/theme';
import DomainChip from '@UI/Pages/Administration/Sso/SsoFormContainer/Fields/DomainChip';
import SectionLabel from '@UI/Pages/Administration/Sso/SsoFormContainer/Shared/SectionLabel';
import { css } from '@emotion/react';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Button, InputText } from '@verclam/ui-kit';

const groupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const domainAdderRowStyle = css`
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
`;

const domainInputStyle = css`
	flex: 1 1 auto;
`;

const domainListStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
`;

const fieldErrorStyle = css`
	margin: 0.5rem 0 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.states.danger.ink};
`;

type Props = {
	readonly domains: readonly string[];
	readonly onChange: (domains: readonly string[]) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
};

/**
 * Edits the list of allowed email domains: owns the pending input, normalizes it (trim + lowercase),
 * refuses duplicates, submits on Enter, and renders the removable chips and the field error.
 */
const EmailDomainsField: React.FC<Props> = ({ domains, onChange, errorMessage, isFormFieldValid }) => {
	const [domainInput, setDomainInput] = React.useState('');

	const handleDomainInputChange = React.useCallback((value: string) => {
		setDomainInput(value);
	}, []);

	const handleAddDomain = React.useCallback(() => {
		const normalizedDomain = domainInput.trim().toLowerCase();

		if (normalizedDomain === '' || domains.includes(normalizedDomain)) {
			setDomainInput('');
			return;
		}

		onChange([...domains, normalizedDomain]);
		setDomainInput('');
	}, [domainInput, domains, onChange]);

	const handleDomainInputKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== 'Enter') {
				return;
			}

			// The field lives inside the `Form`: an unguarded Enter would submit the whole configuration
			// instead of just queuing the domain being typed.
			event.preventDefault();
			handleAddDomain();
		},
		[handleAddDomain],
	);

	const handleRemoveDomain = React.useCallback(
		(domain: string) => {
			onChange(domains.filter((entry) => entry !== domain));
		},
		[domains, onChange],
	);

	return (
		<div css={groupStyle}>
			<SectionLabel>DOMAINES DE MESSAGERIE AUTORISÉS</SectionLabel>
			<div css={domainAdderRowStyle}>
				<div css={domainInputStyle}>
					<InputText
						name="newEmailDomain"
						label="Ajouter un domaine"
						isLabelHidden
						placeholder="exemple.fr"
						value={domainInput}
						onChange={handleDomainInputChange}
						onKeyDown={handleDomainInputKeyDown}
					/>
				</div>
				<Button type="button" label="Ajouter" icon={<Plus size={16} />} onClick={handleAddDomain} />
			</div>
			{domains.length > 0 ? (
				<div css={domainListStyle}>
					{domains.map((domain) => (
						<DomainChip key={domain} domain={domain} onRemove={handleRemoveDomain} />
					))}
				</div>
			) : null}
			{!isFormFieldValid && errorMessage !== undefined ? (
				<p css={fieldErrorStyle}>{errorMessage}</p>
			) : null}
		</div>
	);
};

export default EmailDomainsField;
